/**
 * Employee Document API Contract
 *
 * Endpoints for attaching/detaching company documents to employees.
 * Documents are company-level assets linked via EmployeeDocument junction table,
 * with optional skill linking and folder categorization.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { AttachEmployeeDocumentInputSchema, UpdateEmployeeDocumentInputSchema, EmploymentIdParamsSchema, EmployeeDocumentParamsSchema, EmployeeDocumentResponseSchema, EmployeeDocumentListResponseSchema, ListEmployeeDocumentsQuerySchema, } from "@hourly/api/employeeDocument";
// ============================================
// Contracts
// ============================================
/**
 * GET /employments/:employmentId/documents
 * List all documents attached to an employee
 * Requires: company:view permission (via employment)
 */
export const listEmployeeDocuments = oc
    .route({
    method: "GET",
    path: "/employments/{employmentId}/documents",
    summary: "List documents attached to an employee",
    tags: ["Employee Documents"],
    inputStructure: "detailed",
})
    .input(z.object({
    params: EmploymentIdParamsSchema,
    query: ListEmployeeDocumentsQuerySchema,
}))
    .output(EmployeeDocumentListResponseSchema);
/**
 * POST /employments/:employmentId/documents
 * Attach a document to an employee
 * Requires: company:update permission
 * Returns 201 Created on success
 */
export const attachEmployeeDocument = oc
    .route({
    method: "POST",
    path: "/employments/{employmentId}/documents",
    summary: "Attach a document to an employee",
    tags: ["Employee Documents"],
    successStatus: 201,
    inputStructure: "detailed",
})
    .input(z.object({
    params: EmploymentIdParamsSchema,
    body: AttachEmployeeDocumentInputSchema,
}))
    .output(EmployeeDocumentResponseSchema);
/**
 * GET /employments/:employmentId/documents/:employeeDocumentId
 * Get a single employee document by ID
 * Requires: company:view permission
 */
export const getEmployeeDocument = oc
    .route({
    method: "GET",
    path: "/employments/{employmentId}/documents/{employeeDocumentId}",
    summary: "Get an employee document by ID",
    tags: ["Employee Documents"],
})
    .input(EmployeeDocumentParamsSchema)
    .output(EmployeeDocumentResponseSchema);
/**
 * PATCH /employments/:employmentId/documents/:employeeDocumentId
 * Update employee document metadata (skill link, tag, expiration, notes)
 * Requires: company:update permission
 */
export const updateEmployeeDocument = oc
    .route({
    method: "PATCH",
    path: "/employments/{employmentId}/documents/{employeeDocumentId}",
    summary: "Update employee document metadata",
    tags: ["Employee Documents"],
    inputStructure: "detailed",
})
    .input(z.object({
    params: EmployeeDocumentParamsSchema,
    body: UpdateEmployeeDocumentInputSchema,
}))
    .output(EmployeeDocumentResponseSchema);
/**
 * DELETE /employments/:employmentId/documents/:employeeDocumentId
 * Detach a document from an employee (does NOT delete the document itself)
 * Requires: company:update permission
 * Returns 204 No Content on success
 */
export const detachEmployeeDocument = oc
    .route({
    method: "DELETE",
    path: "/employments/{employmentId}/documents/{employeeDocumentId}",
    summary: "Detach a document from an employee",
    tags: ["Employee Documents"],
    successStatus: 204,
})
    .input(EmployeeDocumentParamsSchema)
    .output(z.void());
/**
 * POST /employments/:employmentId/documents/:employeeDocumentId/acknowledge
 * Employee acknowledges a document. Only the employee themselves can acknowledge.
 * Requires: company:view permission (employment-scoped)
 */
export const acknowledgeEmployeeDocument = oc
    .route({
    method: "POST",
    path: "/employments/{employmentId}/documents/{employeeDocumentId}/acknowledge",
    summary: "Acknowledge a document as the employee",
    tags: ["Employee Documents"],
    successStatus: 200,
    inputStructure: "detailed",
})
    .input(z.object({
    params: EmployeeDocumentParamsSchema,
}))
    .output(EmployeeDocumentResponseSchema);
// ============================================
// Contract Router
// ============================================
export const employeeDocumentContract = {
    list: listEmployeeDocuments,
    attach: attachEmployeeDocument,
    get: getEmployeeDocument,
    update: updateEmployeeDocument,
    detach: detachEmployeeDocument,
    acknowledge: acknowledgeEmployeeDocument,
};
//# sourceMappingURL=employeeDocument.js.map